import { useState, useEffect } from 'react';
import { cloneDeep, isNil, size } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { useTheme } from '@mui/material/styles';
import { Box, Button, Typography, Collapse, TextField } from '@mui/material';

import { ReactComponent as CloseIcon } from 'assets/images/icons/icn_close.svg';
import { ReactComponent as ChevronSolidIcon } from 'assets/images/icons/icn_chevron_solid.svg';

// Utils
import { validUrl } from 'utils/validate';

// Hooks
import useDataSourceManager from 'hooks/useDataSourceManager';
import useAuthScope from 'hooks/useAuthScope';

// Components
import GlobalAlert from 'components/Notification';
import ModalContainer from 'components/ModalContainer';
import { MenuSelect } from 'components/Select/menuSelect';
import { RadioButton } from 'components/buttons/RadioButton';
import { SolidButton } from 'components/buttons/SolidButton';
import { OutlineButton } from 'components/buttons/OutlineButton';

import Loading from '../components/Loading';
import UrlInput from '../components/UrlInput';
import SelectMenu from '../components/SelectMenu';
import TooltipNotice from '../components/TooltipNotice';

// Const
import { captivePortalConfig } from '../const';

const checkMockup = process.env.REACT_APP_IS_MOCKUP && process.env.REACT_APP_IS_MOCKUP === 'true' ? true : false;

const AddCaptivePortalModal = ({ openCreateModal, setOpenCreateModal, tableData, formData, setFormData, reloadPage, initCaptivePortal }) => {
  const theme = useTheme();

  const { auth } = useAuthScope();
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const [createCaptivePortalUuid, _setCreateCaptivePortalUuid] = useState(uuidv4());

  const [hasDuplicateName, setHasDuplicateName] = useState(false);
  const [openGeneral, setOpenGeneral] = useState(true);
  const [openAdvancedSettings, setOpenAdvancedSettings] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: '',
    message: '',
  });

  const textfieldStyle = {
    width: '300px',
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.shape.customBorderRadius[1],
      height: '30px',
      '& fieldset': {
        borderColor: theme.palette.primary.borderGreyPrimary,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.mainHover,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
  };

  const rowStyle = {
    paddingLeft: '32px',
    marginTop: '10px',
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  };

  const labelStyle = {
    width: '200px',
    color: theme.palette.primary.greyPrimary,
    fontSize: theme.typography.customFontSize[2],
    fontWeight: theme.typography.customFontWeight[0],
  };

  const handleChangeItem = (list, newValue) => {
    const newFormData = cloneDeep(formData);
    if (newFormData?.[list]?.length > 0) {
      newFormData[list] = newFormData?.[list]?.map(item => ({
        ...item,
        isActive: item.value === newValue,
      }));
    }
    setFormData(newFormData);
  };

  const handleCloseModal = () => {
    setOpenCreateModal(false);
    setOpenGeneral(true);
    setOpenAdvancedSettings(true);
  };

  const handleClickCreateBtn = () => {
    setIsLoading(true);

    const standalone = !auth.orgs || Object.keys(auth.orgs).length === 0;
    const baseUrl = standalone ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;

    const apiConfig = {
      org: `${baseUrl}/settings/captivePortals.create`,
      site: `${baseUrl}/sites/:siteId/settings/captivePortals.create`,
    };

    const restfulParamsConfig = {
      standalone: {
        orgId: auth.realm?.id,
        siteId: auth.sites?.id,
      },
      nonStandalone: {
        entId: auth.realm?.id,
        orgId: auth.realm?.id,
        siteId: auth.sites?.id,
      },
    };

    const apiKey = apiConfig[auth.scopeLevel];
    const mode = standalone ? 'standalone' : 'nonStandalone';
    const restfulParams = restfulParamsConfig[mode];

    const payload = {
      name: formData?.profileName || '',
      authType: formData?.authTypeList?.find(item => item.isActive)?.value || 'CLICK_THROUGH',
      idleTimeoutOption: formData?.idleTimeoutOption || 'DEFAULT',
      authTimeoutOption: formData?.authTimeoutOption || 'DISABLE',
      authPerDayOption: formData?.authPerDayOption || 'CUSTOM',
      reAuthIntervalOption: formData?.reAuthIntervalOption || 'DISABLE',
      landingPageOption: formData?.landingPageOption || 'NO_SPECIFIC',
      macWhiteList: formData?.whitelistList?.find(item => item.isActive)?.value || '',
    };

    if (formData?.idleTimeoutOption === 'DEFAULT') {
      payload['idleTimeout'] = formData?.idleTimeoutList?.find(item => item.isActive)?.value;
    } else {
      payload['idleTimeout'] = formData?.idleTimeoutLabel; // CUSTOM
    }

    if (formData?.authTimeoutOption === 'ENABLE') {
      payload['authTimeout'] = formData?.authTimeoutList?.find(item => item.isActive)?.value;
    } else if (formData?.authTimeoutOption === 'CUSTOM') {
      payload['authTimeout'] = formData?.authTimeoutLabel;
    } else {
      payload['authTimeout'] = 2; // DISABLE
    }

    if (formData?.authPerDayOption === 'CUSTOM') {
      payload['authPerDay'] = formData?.authPerDayLabel;
    } else {
      payload['authPerDay'] = 1; // UNLIMIT
    }

    if (formData?.reAuthIntervalOption === 'ENABLE') {
      payload['reAuthInterval'] = formData?.reAuthIntervalList?.find(item => item.isActive)?.value;
    } else if (formData?.reAuthIntervalOption === 'CUSTOM') {
      payload['reAuthInterval'] = formData?.reAuthIntervalLabel;
    } else {
      payload['reAuthInterval'] = 1; // DISABLE
    }

    if (formData?.landingPageOption === 'CUSTOM') {
      payload['landingPageUrl'] = `${formData?.landingPageUrlProtocol}://${formData?.landingPageUrlLabel}`;
    } else {
      payload['landingPageUrl'] = '';
    }

    sendRequest(apiKey, createCaptivePortalUuid, restfulParams, payload);
  };

  const checkConfigInvalid = () => {
    const profileNameInvalid = hasDuplicateName || !formData?.profileName;

    const idleTimeoutInvalid = formData?.idleTimeoutOption === 'CUSTOM' && (formData?.idleTimeoutLabel < 2 || formData?.idleTimeoutLabel > 1440);

    const authTimeoutInvalid = formData?.authTimeoutOption === 'CUSTOM' && (formData?.authTimeoutLabel < 2 || formData?.authTimeoutLabel > 1440);

    const authPerDayInvalid = formData?.authPerDayOption === 'CUSTOM' && (formData?.authPerDayLabel < 1 || formData?.authPerDayLabel > 720);

    const reAuthIntervalInvalid = formData?.reAuthIntervalOption === 'CUSTOM' && (formData?.reAuthIntervalLabel < 1 || formData?.reAuthIntervalLabel > 720);

    const landingPageInvalid = formData?.landingPageOption === 'CUSTOM' && (!formData?.landingPageUrlLabel || !validUrl(formData?.landingPageUrlLabel));

    return profileNameInvalid || idleTimeoutInvalid || authTimeoutInvalid || authPerDayInvalid || reAuthIntervalInvalid || landingPageInvalid;
  };

  useEffect(() => {
    if (openCreateModal) {
      setFormData(prev => ({
        ...cloneDeep(captivePortalConfig),
        whitelistList: prev.whitelistList.map((item, index) => ({
          ...item,
          isActive: index === 0,
        })),
      }));
    }
  }, [openCreateModal]);

  useEffect(() => {
    const isCreateResponse = getData(createCaptivePortalUuid);
    if (!isCreateResponse) return;
    setIsLoading(false);

    if (checkMockup) {
      if (isCreateResponse && isCreateResponse?.data) {
        reloadPage(isCreateResponse.data);
        handleCloseModal();
      }
    } else {
      if (isCreateResponse) {
        if (isCreateResponse.hasOwnProperty('code') && isCreateResponse?.code !== 200) {
          setAlertInfo({
            open: true,
            severity: 'error',
            message: isCreateResponse?.message,
          });
        } else {
          initCaptivePortal();
          handleCloseModal();
        }
      }
    }
  }, [dataMap]);

  return (
    <>
      <ModalContainer display={openCreateModal} modalWidth="850px">
        {isLoading && <Loading />}
        <Box sx={{ padding: '0 0 20px 15px' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                color: theme.palette.primary.main,
                fontFamily: theme.typography.fontFamily,
                fontSize: theme.typography.customFontSize[5],
                fontWeight: theme.typography.customFontWeight[3],
              }}
            >
              Add Captive Portal
            </Typography>
            <Button
              onClick={handleCloseModal}
              sx={{
                backgroundColor: theme.palette.primary.transparent,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: theme.palette.primary.transparent,
                },
              }}
            >
              <CloseIcon />
            </Button>
          </Box>

          {/* General */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              paddingTop: '20px',
              cursor: 'pointer',
              gap: '8px',
            }}
            onClick={() => setOpenGeneral(!openGeneral)}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                transform: openGeneral ? 'rotate(90deg)' : 'rotate(0deg)',
              }}
            >
              <ChevronSolidIcon />
            </Box>
            <Typography
              sx={{
                color: theme.palette.primary.greyPrimary,
                fontSize: theme.typography.customFontSize[2],
                fontWeight: theme.typography.customFontWeight[1],
              }}
            >
              General
            </Typography>
          </Box>
          <Collapse in={openGeneral}>
            <Box sx={{ ...rowStyle, marginBottom: hasDuplicateName ? '10px' : '0px' }}>
              <Box sx={{ ...labelStyle, paddingTop: hasDuplicateName ? '0px' : '14px', height: hasDuplicateName ? '42px' : '50px' }}>Profile Name</Box>
              <Box>
                <TextField
                  placeholder="e.g. Pro WiFi Access"
                  sx={textfieldStyle}
                  autoComplete="off"
                  inputProps={{
                    minLength: 1,
                    maxLength: 64,
                  }}
                  value={formData?.profileName || ''}
                  onChange={e => {
                    setFormData(prev => ({ ...prev, profileName: e.target.value }));
                    setHasDuplicateName(tableData?.some(item => item.captivePortalName === e.target.value));
                  }}
                  error={hasDuplicateName || (!isNil(formData?.profileName) && size(formData?.profileName) === 0)}
                  helperText={hasDuplicateName ? 'This name already exists in this site.' : ''}
                />
              </Box>
            </Box>
            <Box sx={rowStyle}>
              <Box sx={{ ...labelStyle, paddingTop: '5px', height: '30px' }}>Authentication Type</Box>
              <MenuSelect
                width={textfieldStyle.width}
                value={formData?.authTypeList?.find(item => item.isActive)?.value}
                options={formData?.authTypeList}
                onChange={obj => handleChangeItem('authTypeList', obj.value)}
              />
            </Box>
            <Box sx={rowStyle}>
              <Box sx={{ ...labelStyle, height: '50px' }}>Idle Timeout</Box>
              <Box>
                <div style={{ display: 'flex' }}>
                  <div style={{ width: 100 }}>
                    <RadioButton
                      label="Default"
                      name="idleTimeoutOption"
                      value={'DEFAULT'}
                      checked={formData?.idleTimeoutOption === 'DEFAULT'}
                      onChange={() => setFormData(prev => ({ ...prev, idleTimeoutOption: 'DEFAULT' }))}
                    />
                  </div>
                  <div style={{ width: 100 }}>
                    <RadioButton
                      label="Custom"
                      name="idleTimeoutOption"
                      value={'CUSTOM'}
                      checked={formData?.idleTimeoutOption === 'CUSTOM'}
                      onChange={() => setFormData(prev => ({ ...prev, idleTimeoutOption: 'CUSTOM' }))}
                    />
                  </div>
                </div>
                {formData?.idleTimeoutOption === 'DEFAULT' && (
                  <MenuSelect
                    width={textfieldStyle.width}
                    value={formData?.idleTimeoutList?.find(item => item.isActive)?.value}
                    options={formData?.idleTimeoutList}
                    onChange={obj => handleChangeItem('idleTimeoutList', obj.value)}
                  />
                )}
                {formData?.idleTimeoutOption === 'CUSTOM' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        right: 90,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#aaa',
                        pointerEvents: 'none',
                      }}
                    >
                      Min(s)
                    </Box>

                    <TextField
                      sx={textfieldStyle}
                      autoComplete="off"
                      type="number"
                      min="2"
                      max="1440"
                      value={formData?.idleTimeoutLabel}
                      onChange={e => setFormData(prev => ({ ...prev, idleTimeoutLabel: e.target.value === '' ? '' : Number(e.target.value) }))}
                      error={formData?.idleTimeoutLabel < 2 || formData?.idleTimeoutLabel > 1440}
                    />
                    <Typography sx={{ marginLeft: '10px', color: theme.palette.primary.greySecound }}>(2~1440)</Typography>
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={rowStyle}>
              <Box sx={{ ...labelStyle, height: formData?.authTimeoutOption === 'DISABLE' ? '20px' : '50px' }}>Authentication Timeout</Box>
              <Box>
                <div style={{ display: 'flex' }}>
                  <div style={{ width: 100 }}>
                    <RadioButton
                      label="Disabled"
                      name="authTimeoutOption"
                      value={'DISABLE'}
                      checked={formData?.authTimeoutOption === 'DISABLE'}
                      onChange={() => setFormData(prev => ({ ...prev, authTimeoutOption: 'DISABLE' }))}
                    />
                  </div>
                  <div style={{ width: 100 }}>
                    <RadioButton
                      label="Enabled"
                      name="authTimeoutOption"
                      value={'ENABLE'}
                      checked={formData?.authTimeoutOption === 'ENABLE'}
                      onChange={() => setFormData(prev => ({ ...prev, authTimeoutOption: 'ENABLE' }))}
                    />
                  </div>
                  <div style={{ width: 100 }}>
                    <RadioButton
                      label="Custom"
                      name="authTimeoutOption"
                      value={'CUSTOM'}
                      checked={formData?.authTimeoutOption === 'CUSTOM'}
                      onChange={() => setFormData(prev => ({ ...prev, authTimeoutOption: 'CUSTOM' }))}
                    />
                  </div>
                </div>

                {formData?.authTimeoutOption === 'ENABLE' && (
                  <MenuSelect
                    width={textfieldStyle.width}
                    value={formData?.authTimeoutList?.find(item => item.isActive)?.value}
                    options={formData?.authTimeoutList}
                    onChange={obj => handleChangeItem('authTimeoutList', obj.value)}
                  />
                )}
                {formData?.authTimeoutOption === 'CUSTOM' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        right: 90,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#aaa',
                        pointerEvents: 'none',
                      }}
                    >
                      Min(s)
                    </Box>
                    <TextField
                      sx={textfieldStyle}
                      autoComplete="off"
                      type="number"
                      min="2"
                      max="1440"
                      value={formData?.authTimeoutLabel}
                      onChange={e => setFormData(prev => ({ ...prev, authTimeoutLabel: e.target.value === '' ? '' : Number(e.target.value) }))}
                      error={formData?.authTimeoutLabel < 2 || formData?.authTimeoutLabel > 1440}
                    />
                    <Typography sx={{ marginLeft: '10px', color: theme.palette.primary.greySecound }}>(2~1440)</Typography>
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={rowStyle}>
              <Box sx={{ ...labelStyle, height: formData?.authPerDayOption === 'CUSTOM' ? '50px' : '20px' }}>Authentication Per Day</Box>
              <Box>
                <div style={{ display: 'flex' }}>
                  <div style={{ width: 100 }}>
                    <RadioButton
                      label="Custom"
                      name="authPerDayOption"
                      value={'CUSTOM'}
                      checked={formData?.authPerDayOption === 'CUSTOM'}
                      onChange={() => setFormData(prev => ({ ...prev, authPerDayOption: 'CUSTOM' }))}
                    />
                  </div>
                  <div style={{ width: 100 }}>
                    <RadioButton
                      label="Unlimited"
                      name="authPerDayOption"
                      value={'UNLIMIT'}
                      checked={formData?.authPerDayOption === 'UNLIMIT'}
                      onChange={() => setFormData(prev => ({ ...prev, authPerDayOption: 'UNLIMIT' }))}
                    />
                  </div>
                </div>

                {formData?.authPerDayOption === 'CUSTOM' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        right: 80,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#aaa',
                        pointerEvents: 'none',
                      }}
                    >
                      Time/Day
                    </Box>
                    <TextField
                      sx={textfieldStyle}
                      autoComplete="off"
                      type="number"
                      min="1"
                      max="720"
                      value={formData?.authPerDayLabel}
                      onChange={e => setFormData(prev => ({ ...prev, authPerDayLabel: e.target.value === '' ? '' : Number(e.target.value) }))}
                      error={formData?.authPerDayLabel < 1 || formData?.authPerDayLabel > 720}
                    />
                    <Typography sx={{ marginLeft: '10px', color: theme.palette.primary.greySecound }}>(1~720)</Typography>
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={rowStyle}>
              <Box sx={{ ...labelStyle, height: formData?.reAuthIntervalOption === 'DISABLE' ? '20px' : '50px' }}>Re-Authentication Interval</Box>
              <Box>
                <div style={{ display: 'flex' }}>
                  <div style={{ width: 100 }}>
                    <RadioButton
                      label="Disabled"
                      name="reAuthIntervalOption"
                      value={'DISABLE'}
                      checked={formData?.reAuthIntervalOption === 'DISABLE'}
                      onChange={() => setFormData(prev => ({ ...prev, reAuthIntervalOption: 'DISABLE' }))}
                    />
                  </div>
                  <div style={{ width: 100 }}>
                    <RadioButton
                      label="Enabled"
                      name="reAuthIntervalOption"
                      value={'ENABLE'}
                      checked={formData?.reAuthIntervalOption === 'ENABLE'}
                      onChange={() => setFormData(prev => ({ ...prev, reAuthIntervalOption: 'ENABLE' }))}
                    />
                  </div>
                  <div style={{ width: 100 }}>
                    <RadioButton
                      label="Custom"
                      name="reAuthIntervalOption"
                      value={'CUSTOM'}
                      checked={formData?.reAuthIntervalOption === 'CUSTOM'}
                      onChange={() => setFormData(prev => ({ ...prev, reAuthIntervalOption: 'CUSTOM' }))}
                    />
                  </div>
                </div>

                {formData?.reAuthIntervalOption === 'ENABLE' && (
                  <MenuSelect
                    width={textfieldStyle.width}
                    value={formData?.reAuthIntervalList?.find(item => item.isActive)?.value}
                    options={formData?.reAuthIntervalList}
                    onChange={obj => handleChangeItem('reAuthIntervalList', obj.value)}
                  />
                )}
                {formData?.reAuthIntervalOption === 'CUSTOM' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        right: 80,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#aaa',
                        pointerEvents: 'none',
                      }}
                    >
                      Min(s)
                    </Box>
                    <TextField
                      sx={textfieldStyle}
                      autoComplete="off"
                      type="number"
                      min="1"
                      max="720"
                      value={formData?.reAuthIntervalLabel}
                      onChange={e => setFormData(prev => ({ ...prev, reAuthIntervalLabel: e.target.value === '' ? '' : Number(e.target.value) }))}
                      error={formData?.reAuthIntervalLabel < 1 || formData?.reAuthIntervalLabel > 720}
                    />
                    <Typography sx={{ marginLeft: '10px', color: theme.palette.primary.greySecound }}>(1~720)</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Collapse>

          {/* Advanced Settings */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              paddingTop: '20px',
              cursor: 'pointer',
              gap: '8px',
            }}
            onClick={() => setOpenAdvancedSettings(!openAdvancedSettings)}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                transform: openAdvancedSettings ? 'rotate(90deg)' : 'rotate(0deg)',
              }}
            >
              <ChevronSolidIcon />
            </Box>
            <Typography
              sx={{
                color: theme.palette.primary.greyPrimary,
                fontSize: theme.typography.customFontSize[2],
                fontWeight: theme.typography.customFontWeight[1],
              }}
            >
              Advanced Settings
            </Typography>
          </Box>
          <Collapse in={openAdvancedSettings}>
            <Box sx={{ ...rowStyle, alignItems: 'flex-start' }}>
              <Box sx={{ ...labelStyle, paddingTop: '10px', display: 'flex' }}>
                <Typography sx={{ marginRight: 1 }}>Landing Page</Typography>
                <TooltipNotice placement="bottom" tooltipTitle={<Box sx={{ width: 210 }}>Clients will be directed to the page after they pass Portal authentication.</Box>} />
              </Box>
              <Box sx={{ display: 'grid' }}>
                <RadioButton
                  label="Not Specified"
                  name="landingPageOption"
                  value={'NO_SPECIFIC'}
                  checked={formData?.landingPageOption === 'NO_SPECIFIC'}
                  onChange={() => setFormData(prev => ({ ...prev, landingPageOption: 'NO_SPECIFIC' }))}
                />
                <RadioButton
                  label="The Promotional URL"
                  name="landingPageOption"
                  value={'CUSTOM'}
                  checked={formData?.landingPageOption === 'CUSTOM'}
                  onChange={() => setFormData(prev => ({ ...prev, landingPageOption: 'CUSTOM' }))}
                />
                {formData?.landingPageOption === 'CUSTOM' && (
                  <Box sx={{ width: '200px', marginBottom: '10px' }}>
                    <UrlInput
                      maxLength={formData?.landingPageUrlProtocol === 'https' ? 2075 : 2076} // Custom landing page URL max 2083 characters.
                      placeholder="www.dlink.com"
                      protocol={formData?.landingPageUrlProtocol}
                      onChangeProtocol={protocol => setFormData(prev => ({ ...prev, landingPageUrlProtocol: protocol }))}
                      inputValue={formData?.landingPageUrlLabel || ''}
                      onChangeInputValue={newUrlInputValue => setFormData(prev => ({ ...prev, landingPageUrlLabel: newUrlInputValue }))}
                      error={
                        (!isNil(formData?.landingPageUrlLabel) && size(formData?.landingPageUrlLabel) === 0) ||
                        (size(formData?.landingPageUrlLabel) > 0 && !validUrl(formData?.landingPageUrlLabel))
                      }
                    />
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={{ ...rowStyle, alignItems: 'flex-start' }}>
              <Box sx={{ ...labelStyle, paddingTop: '4px', display: 'flex' }}>
                <Typography sx={{ marginRight: 1 }}>Whitelist</Typography>
                <TooltipNotice
                  placement="bottom"
                  tooltipTitle={<Box sx={{ width: 230 }}>To set up the Whitelist, add one or more existing MAC Address Profiles. A maximum of 64 profiles can be configured.</Box>}
                />
              </Box>

              <SelectMenu menuList={formData?.whitelistList} onChangeItem={newValue => handleChangeItem('whitelistList', newValue)} />
            </Box>
          </Collapse>
        </Box>
        <Box
          sx={{
            paddingTop: '20px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <OutlineButton sx={{ marginRight: '20px' }} width="100px" height="32px" onClick={handleCloseModal}>
            Cancel
          </OutlineButton>
          <SolidButton width="100px" height="32px" onClick={handleClickCreateBtn} disabled={checkConfigInvalid()}>
            Create
          </SolidButton>
        </Box>
      </ModalContainer>

      <GlobalAlert open={alertInfo.open} severity={alertInfo.severity} message={alertInfo.message} onClose={() => setAlertInfo(prev => ({ ...prev, open: false }))} />
    </>
  );
};

export default AddCaptivePortalModal;
